/**
 * Blademaster talents (melee DPS), reworked for the rhythm pass (docs/design/classes/blademaster.md §5).
 * Every node changes a button or a proc of the Wounds / Tactician / Sudden Death loop:
 *  - tier 1: execute window, Overpower charges, AoE reach, the Shockwave transform (Cleave turns
 *    Slam into Shockwave), utility that feeds the procs;
 *  - tier 2: Rage and cooldown engines (Test of Might, Unhinged, Battlelord) plus self-sustain;
 *  - tier 3: Executioner's Precision, Warbreaker, and three capstones: Ravager's Reach (AoE),
 *    Deep Wounds (single target) and Unbreakable Warlord (bruiser).
 *
 * Auras the class already defines are copied with `auraRef` so every inline copy stays identical.
 */
import { ABILITY, AURA, CLASS } from '@keystone/content';
import type { AbilityDef, AreaSpec, AuraDef, Condition, EffectSpec } from '@keystone/sim';
import { auraRef } from '../../transforms';
import type { TalentNode } from '../../types';
import { nodeFor, procPassive, resetCd, resource, selfAura, stun } from './common';

const B = ABILITY.blademaster;
const node = nodeFor(CLASS.blademaster);
export const BLADEMASTER_CAPSTONE = 'blademaster.capstone';

/** Talent-only transform form of Slam (offered by Shockwave). Clip falls back to Slam. */
export const SHOCKWAVE_ID = 'blademaster.shockwave';
const EP_ID = 'talent.blademaster.executioners_precision';
const BATTLELORD_ID = 'talent.blademaster.battlelord';

const sd = (): AuraDef => auraRef(AURA.suddenDeath);
const wounds = (): AuraDef => auraRef(AURA.wounds);
const sweepingEdge = (): AuraDef => auraRef(AURA.cleaveBuff);
const fromSelf = (auraId: string, minStacks?: number): Condition => ({ type: 'targetHasAura', auraId, fromSelf: true, ...(minStacks ? { minStacks } : {}) });

/** Sweeping Edge holds 2 stacks (one per cleaving Mortal Strike). Shared by two nodes: keep identical. */
const SWEEPING_EDGE_STACKS = { op: 'patchAura', auraId: AURA.cleaveBuff, patch: { stacking: 'stack', maxStacks: 2 } } as const;

/** "Heal 1% max HP per Wounds stack on the target" as one heal (nested conditionals, 5 → 1). */
function healPerWounds(pctPerStack: number, maxStacks: number): EffectSpec {
  let chain: EffectSpec | undefined;
  for (let k = 1; k <= maxStacks; k++) {
    chain = {
      type: 'conditional',
      cond: fromSelf(AURA.wounds, k),
      then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: pctPerStack * k } }],
      ...(chain ? { else: [chain] } : {}),
    };
  }
  return chain!;
}

const SHOCKWAVE_AREA: AreaSpec = { anchor: 'caster', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 8 };
const SHOCKWAVE_REND_AREA: AreaSpec = { ...SHOCKWAVE_AREA, maxTargets: 5 };

const shockwave: AbilityDef = {
  id: SHOCKWAVE_ID,
  name: 'Shockwave',
  school: 'physical',
  description: 'Slam the ground, hitting up to 8 enemies within 8 yd, applying Rend to up to 5 of them and 1 Wounds to all.',
  targeting: 'none',
  cost: [{ resource: 'rage', amount: 10 }],
  effects: [
    { type: 'damage', to: 'area', area: SHOCKWAVE_AREA, school: 'physical', amount: { coef: 1.6, variance: 0.05 } },
    { type: 'applyAura', to: 'area', area: SHOCKWAVE_REND_AREA, aura: auraRef(AURA.rend) },
    { type: 'applyAura', to: 'area', area: SHOCKWAVE_AREA, aura: wounds() },
    { type: 'removeAura', to: 'self', auraId: AURA.shockwaveReady },
  ],
  ui: { tags: ['aoe'], color: '#a04000', animAs: B.slam },
};

const SHOCKWAVE_READY: AuraDef = {
  id: AURA.shockwaveReady,
  name: 'Shockwave',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  overrides: [{ abilityId: B.slam, with: SHOCKWAVE_ID }],
  ui: { proc: true },
};

const BLADESTORM_END_AREA: AreaSpec = { anchor: 'caster', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 8 };

export const BLADEMASTER_TREE: readonly TalentNode[] = [
  // ---------------------------------------------------------------- tier 1
  node({
    id: 'blademaster.t1.execute_range',
    name: 'Massacre',
    description: 'Execute can be used on targets below 35% health.',
    tier: 1,
    col: 0,
    cost: 2,
    effect: [
      {
        op: 'patchAbility',
        abilityId: B.execute,
        patch: { usableIf: { type: 'or', any: [{ type: 'targetHpBelow', pct: 0.35 }, { type: 'selfHasAura', auraId: AURA.suddenDeath }] } },
      },
    ],
  }),
  node({
    id: 'blademaster.t1.tactical_reserve',
    name: 'Tactical Reserve',
    description: 'Overpower holds a 3rd charge, and a Tactician-empowered Overpower applies 2 more Wounds (4).',
    tier: 1,
    col: 1,
    cost: 2,
    effect: [
      { op: 'modifyCooldown', abilityId: B.overpower, charges: 3 },
      { op: 'addEffects', abilityId: B.overpower, effects: [{ type: 'conditional', cond: { type: 'selfHasAura', auraId: AURA.overpowerProc }, then: [{ type: 'applyAura', aura: wounds(), stacks: 2 }] }] },
    ],
  }),
  node({
    id: 'blademaster.t1.sweeping_strikes',
    name: 'Sweeping Strikes',
    description: 'Cleave hits up to 8 enemies, and Sweeping Edge holds 2 stacks: two cleaving Mortal Strikes per Cleave.',
    tier: 1,
    col: 2,
    cost: 2,
    effect: [
      { op: 'modifyArea', abilityId: B.cleave, maxTargetsDelta: 3 },
      SWEEPING_EDGE_STACKS,
      { op: 'addEffects', abilityId: B.cleave, effects: [{ type: 'applyAura', to: 'self', aura: sweepingEdge() }] },
    ],
  }),
  node({
    id: 'blademaster.t1.shockwave',
    name: 'Shockwave',
    description: 'Cleave turns Slam into Shockwave for 8 s: hits up to 8 enemies within 8 yd, applies Rend to 5 of them and 1 Wounds to all.',
    tier: 1,
    col: 3,
    cost: 2,
    effect: [
      { op: 'addAbility', ability: shockwave },
      { op: 'addEffects', abilityId: B.cleave, effects: [{ type: 'applyAura', to: 'self', aura: SHOCKWAVE_READY }] },
    ],
  }),
  node({
    id: 'blademaster.t1.warbringer',
    name: 'Warbringer',
    description: 'Charge also stuns the target for 1.5 s.',
    tier: 1,
    col: 4,
    cost: 1,
    effect: [{ op: 'addEffects', abilityId: B.charge, effects: [{ type: 'applyAura', aura: stun('talent.blademaster.warbringer', 'Warbringer', 1500) }] }],
  }),
  node({
    id: 'blademaster.t1.disrupting_pummel',
    name: 'Disrupting Pummel',
    description: 'A successful Pummel interrupt grants a stack of Sudden Death.',
    tier: 1,
    col: 5,
    cost: 1,
    effect: [
      procPassive('talent.blademaster.disrupting_pummel', 'Disrupting Pummel', {
        on: 'interruptDealt',
        abilityIds: [B.pummel],
        effects: [{ type: 'applyAura', to: 'self', aura: sd() }],
      }),
    ],
  }),
  node({
    id: 'blademaster.t1.bloodcraze',
    name: 'Bloodcraze',
    description: 'While Die by the Sword is up, each parry grants 5 Rage and Tactician (once every 3 s).',
    tier: 1,
    col: 6,
    cost: 1,
    effect: [
      procPassive('talent.blademaster.bloodcraze', 'Bloodcraze', {
        on: 'avoided',
        hitTypes: ['parry'],
        icdMs: 3000,
        if: { type: 'selfHasAura', auraId: AURA.dieByTheSword },
        effects: [
          resource('rage', 5),
          { type: 'modifyCooldown', to: 'self', abilityId: B.overpower, addCharge: true },
          { type: 'applyAura', to: 'self', aura: auraRef(AURA.overpowerProc) },
        ],
      }),
    ],
  }),

  // ---------------------------------------------------------------- tier 2
  node({
    id: 'blademaster.t2.fervor_of_battle',
    name: 'Fervor of Battle',
    description: 'Mortal Strike cleaves without needing Cleave first whenever 2 or more enemies are within 8 yd of you.',
    tier: 2,
    col: 0,
    cost: 3,
    prereqs: ['blademaster.t1.sweeping_strikes'],
    // A cast proc runs before Mortal Strike's own effects, so granting Sweeping Edge here makes
    // the existing cleave branch fire (and consume it) without duplicating Mortal Strike's numbers.
    effect: [
      procPassive('talent.blademaster.fervor_of_battle', 'Fervor of Battle', {
        on: 'cast',
        abilityIds: [B.mortalStrike],
        effects: [{ type: 'conditional', cond: { type: 'enemiesNearSelf', radius: 8, atLeast: 2 }, then: [{ type: 'applyAura', to: 'self', aura: sweepingEdge() }] }],
      }),
    ],
  }),
  node({
    id: 'blademaster.t2.test_of_might',
    name: 'Test of Might',
    description: "Colossus Smash generates 20 Rage and sets the target's Wounds to 5.",
    tier: 2,
    col: 1,
    cost: 3,
    effect: [
      { op: 'modifyResource', abilityId: B.colossusSmash, field: 'gain', resource: 'rage', amount: 20 },
      { op: 'addEffects', abilityId: B.colossusSmash, effects: [{ type: 'applyAura', aura: wounds(), stacks: 5 }] },
    ],
  }),
  node({
    id: 'blademaster.t2.unhinged',
    name: 'Unhinged',
    description: "Bladestorm generates 4 Rage per spin. Ravager's Finale stays available for 15 s and refills your Overpower charges.",
    tier: 2,
    col: 2,
    cost: 2,
    effect: [
      { op: 'addEffects', abilityId: B.bladestorm, effects: [resource('rage', 4)] },
      { op: 'patchAura', auraId: AURA.ravagersFinaleReady, patch: { durationMs: 15000 } },
      { op: 'addEffects', abilityId: B.ravagersFinale, effects: [resetCd(B.overpower)] },
    ],
  }),
  node({
    id: 'blademaster.t2.battlelord',
    name: 'Battlelord',
    description: "Overpower has a 12% chance (rising after each miss) to reset Mortal Strike's cooldown and make it deal double damage for 8 s.",
    tier: 2,
    col: 3,
    cost: 2,
    effect: [
      procPassive('talent.blademaster.battlelord_proc', 'Battlelord', {
        on: 'castResolved',
        abilityIds: [B.overpower],
        chance: 0.12,
        badLuck: { step: 0.04 },
        effects: [
          resetCd(B.mortalStrike),
          selfAura({
            id: BATTLELORD_ID,
            name: 'Battlelord',
            durationMs: 8000,
            mods: [{ stat: 'damageDonePct', value: 1, abilityIds: [B.mortalStrike] }],
            procs: [{ on: 'castResolved', abilityIds: [B.mortalStrike], consumeStack: true, effects: [] }],
            ui: { proc: true },
          }),
        ],
      }),
    ],
  }),
  node({
    id: 'blademaster.t2.second_wind',
    name: 'Second Wind',
    description: 'Taking damage below 35% health heals you for 12% of your max HP (once every 30 s).',
    tier: 2,
    col: 4,
    cost: 2,
    effect: [
      procPassive('talent.blademaster.second_wind', 'Second Wind', {
        on: 'damageTaken',
        icdMs: 30000,
        effects: [{ type: 'conditional', cond: { type: 'selfHpBelow', pct: 0.35 }, then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.12 } }] }],
      }),
    ],
  }),
  node({
    id: 'blademaster.t2.impending_victory',
    name: 'Impending Victory',
    description: 'Executes empowered by Sudden Death heal you for 5% of your max HP.',
    tier: 2,
    col: 5,
    cost: 1,
    effect: [
      {
        op: 'addEffects',
        abilityId: B.execute,
        effects: [{ type: 'conditional', cond: { type: 'selfHasAura', auraId: AURA.suddenDeath }, then: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.05 } }] }],
      },
    ],
  }),

  // ---------------------------------------------------------------- tier 3
  node({
    id: 'blademaster.t3.executioners_precision',
    name: "Executioner's Precision",
    description: "Execute applies Executioner's Precision (2 stacks, 15 s); your next Mortal Strike consumes it for +1.0 damage coefficient per stack.",
    tier: 3,
    col: 0,
    cost: 3,
    prereqs: ['blademaster.t1.execute_range'],
    effect: [
      {
        op: 'addEffects',
        abilityId: B.execute,
        effects: [{ type: 'applyAura', aura: { id: EP_ID, name: "Executioner's Precision", kind: 'debuff', durationMs: 15000, stacking: 'stack', maxStacks: 2, ui: { glow: { abilityIds: [B.mortalStrike] }, stackCounterOn: [B.mortalStrike] } } }],
      },
      {
        op: 'addEffects',
        abilityId: B.mortalStrike,
        position: 'start',
        effects: [
          {
            type: 'conditional',
            cond: fromSelf(EP_ID, 2),
            then: [{ type: 'damage', school: 'physical', amount: { coef: 2.0, variance: 0.05 } }],
            else: [{ type: 'damage', if: fromSelf(EP_ID), school: 'physical', amount: { coef: 1.0, variance: 0.05 } }],
          },
          { type: 'removeAura', auraId: EP_ID },
        ],
      },
    ],
  }),
  node({
    id: 'blademaster.t3.warbreaker',
    name: 'Warbreaker',
    description: 'Colossus Smash becomes Warbreaker: it hits every enemy within 8 yd of you for the same damage and applies 2 Wounds to each.',
    tier: 3,
    col: 1,
    cost: 2,
    // A permanent rewrite of Colossus Smash that keeps its id and clip. It still needs an enemy
    // target (so Test of Might's "target to 5 Wounds" keeps working); the hits are around you.
    effect: [
      { op: 'removeEffects', abilityId: B.colossusSmash, effectType: 'damage' },
      { op: 'removeEffects', abilityId: B.colossusSmash, effectType: 'applyAura', index: 0 },
      { op: 'patchAbility', abilityId: B.colossusSmash, patch: { name: 'Warbreaker' } },
      {
        op: 'addEffects',
        abilityId: B.colossusSmash,
        position: 'start',
        effects: [
          { type: 'damage', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 8 }, affects: 'enemies' }, school: 'physical', amount: { coef: 3.0, variance: 0.05 } },
          { type: 'applyAura', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 8 }, affects: 'enemies' }, aura: wounds(), stacks: 2 },
        ],
      },
    ],
  }),
  node({
    id: 'blademaster.t3.ravagers_reach',
    name: "Ravager's Reach",
    description: "Capstone. Bladestorm hits up to 12 enemies in a 25% larger area. Ravager's Finale hits up to 12 and grants 2 stacks of Sweeping Edge.",
    tier: 3,
    col: 2,
    cost: 3,
    choiceGroup: BLADEMASTER_CAPSTONE,
    effect: [
      { op: 'modifyArea', abilityId: B.bladestorm, maxTargetsDelta: 4, radiusMult: 1.25 },
      { op: 'modifyArea', abilityId: B.bladestorm, maxTargetsDelta: 4, radiusMult: 1.25, list: 'channelEndEffects' },
      { op: 'modifyArea', abilityId: B.ravagersFinale, maxTargetsDelta: 4 },
      SWEEPING_EDGE_STACKS,
      { op: 'addEffects', abilityId: B.ravagersFinale, effects: [{ type: 'applyAura', to: 'self', aura: sweepingEdge(), stacks: 2 }] },
    ],
  }),
  node({
    id: 'blademaster.t3.executioner',
    name: 'Deep Wounds',
    description: "Capstone. Wounds stack up to 7 (Bladestorm's end sets 7), and a Mortal Strike that consumes 5 or more grants a stack of Sudden Death.",
    tier: 3,
    col: 3,
    cost: 3,
    choiceGroup: BLADEMASTER_CAPSTONE,
    effect: [
      { op: 'patchAura', auraId: AURA.wounds, patch: { maxStacks: 7 } },
      { op: 'addEffects', abilityId: B.bladestorm, list: 'channelEndEffects', position: 'start', effects: [{ type: 'applyAura', to: 'area', area: BLADESTORM_END_AREA, aura: wounds(), stacks: 2 }] },
      {
        op: 'addEffects',
        abilityId: B.mortalStrike,
        position: 'start',
        effects: [{ type: 'conditional', cond: fromSelf(AURA.wounds, 5), then: [{ type: 'applyAura', to: 'self', aura: sd() }] }],
      },
    ],
  }),
  node({
    id: 'blademaster.t3.unbreakable',
    name: 'Unbreakable Warlord',
    description: 'Capstone. Die by the Sword recharges in 30 s, and Mortal Strike heals you for 1% of your max HP per Wounds stack it consumes.',
    tier: 3,
    col: 4,
    cost: 3,
    choiceGroup: BLADEMASTER_CAPSTONE,
    effect: [
      { op: 'modifyCooldown', abilityId: B.dieByTheSword, durationMs: 30000 },
      { op: 'addEffects', abilityId: B.mortalStrike, position: 'start', effects: [healPerWounds(0.01, 5)] },
    ],
  }),
];

